import React, { useState } from "react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { X } from "lucide-react";

const MIN_TABLE_PRICE = 25;
const half = (n) => Math.round(n * 50) / 100;

const initForm = (table, events) => table ? {
  event_id: table.event_id, name: table.name, capacity: table.capacity,
  table_price: table.table_price ?? table.price_per_person ?? MIN_TABLE_PRICE,
  entry_price: table.entry_price ?? 0,
  deposit_per_person: table.deposit_per_person === half(table.price_per_person ?? 0) ? "" : table.deposit_per_person,
  is_public: table.is_public, note: table.note || "",
} : {
  event_id: events[0]?.id || "", name: "", capacity: 6, table_price: MIN_TABLE_PRICE,
  entry_price: events[0]?.entry_price ?? 0, deposit_per_person: "", is_public: true, note: "",
};

function NumField({ label, testid, ...rest }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <input type="number" step="0.01" className="input-dark disabled:opacity-60" data-testid={testid} {...rest}/>
    </div>
  );
}

function PriceSummary({ entry, tablePrice, deposit }) {
  return (
    <div className="border border-neutral-800 p-4 font-mono text-xs tracking-widest space-y-1" data-testid="table-price-summary">
      <div className="flex justify-between text-neutral-400"><span>INGRESSO</span><span>€{entry}</span></div>
      <div className="flex justify-between text-neutral-400"><span>TAVOLO</span><span>€{tablePrice}</span></div>
      <div className="flex justify-between text-white"><span>TOTALE A PERSONA</span><span data-testid="table-total-preview">€{Math.round((entry + tablePrice) * 100) / 100}</span></div>
      <div className="flex justify-between text-neon"><span>CAPARRA A PERSONA</span><span data-testid="table-deposit-preview">€{deposit}</span></div>
    </div>
  );
}

export default function CreateTableModal({ events, table, onClose, onCreated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isStaff = isAdmin || user?.role === "affiliate";
  const [form, setForm] = useState(() => initForm(table, events));
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const pickEvent = (e) => {
    const ev = events.find(x => x.id === e.target.value);
    setForm({ ...form, event_id: e.target.value, entry_price: ev?.entry_price ?? 0 });
  };
  const entry = Number(form.entry_price) || 0;
  const tablePrice = Number(form.table_price) || 0;
  const autoDeposit = half(entry + tablePrice);
  const deposit = isStaff && form.deposit_per_person !== "" ? Number(form.deposit_per_person) : autoDeposit;

  const submit = async (e) => {
    e.preventDefault();
    if (!isStaff && tablePrice < MIN_TABLE_PRICE) { toast.error(`Il prezzo del tavolo deve essere almeno €${MIN_TABLE_PRICE}`); return; }
    if (deposit > entry + tablePrice) { toast.error("La caparra non può superare il totale a persona"); return; }
    setLoading(true);
    const body = {
      name: form.name, capacity: Number(form.capacity), table_price: tablePrice, is_public: form.is_public, note: form.note,
      entry_price: isAdmin ? entry : null,
      deposit_per_person: isStaff && form.deposit_per_person !== "" ? Number(form.deposit_per_person) : null,
    };
    try {
      if (table) await api.put(`/tables/${table.id}`, body);
      else await api.post("/tables", { ...body, event_id: form.event_id });
      toast.success(table ? "Tavolo aggiornato" : "Tavolo creato!");
      onCreated();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-neutral-950 border border-neutral-800 w-full max-w-md p-8 relative max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="create-table-modal">
        <button className="absolute top-4 right-4 text-neutral-500 hover:text-white" onClick={onClose} data-testid="create-table-close" aria-label="Chiudi"><X/></button>
        <div className="label-mono text-neon mb-2">{table ? "MODIFICA TAVOLO" : "NUOVO TAVOLO"}</div>
        <h3 className="font-display text-2xl mb-6">{table ? "MODIFICA" : "CREA"}</h3>
        <form onSubmit={submit} className="space-y-5">
          {!table && (
            <div>
              <div className="label-mono mb-1">EVENTO</div>
              <select required value={form.event_id} onChange={pickEvent} className="input-dark bg-black" data-testid="table-event">
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title} — {ev.venue}</option>)}
              </select>
            </div>
          )}
          <div><div className="label-mono mb-1">NOME TAVOLO</div><input required value={form.name} onChange={set("name")} className="input-dark" data-testid="table-name"/></div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <NumField label="POSTI" testid="table-capacity" required min="2" max="20" step="1" value={form.capacity} onChange={set("capacity")}/>
            <NumField label="INGRESSO €" testid="table-entry" disabled={!isAdmin} min="0" value={form.entry_price} onChange={set("entry_price")}/>
            <NumField label={`TAVOLO € ${isStaff ? "" : `(MIN ${MIN_TABLE_PRICE})`}`} testid="table-price" required min={isStaff ? 0 : MIN_TABLE_PRICE} value={form.table_price} onChange={set("table_price")}/>
            {isStaff && <NumField label="CAPARRA €" testid="table-deposit" min="1" placeholder={`Auto €${autoDeposit}`} value={form.deposit_per_person} onChange={set("deposit_per_person")}/>}
          </div>
          <PriceSummary entry={entry} tablePrice={tablePrice} deposit={deposit}/>
          <p className="text-[11px] text-neutral-500 -mt-2">
            L'ingresso è fisso e lo decide il locale. La caparra è il 50% del totale{isStaff ? " (lascia vuoto per il calcolo automatico)" : ""}: si paga online, il resto al locale. In caso di recesso la caparra non è rimborsabile.
          </p>
          <div><div className="label-mono mb-1">NOTE</div><input value={form.note} onChange={set("note")} className="input-dark" data-testid="table-note"/></div>
          <label className="flex items-center gap-3 text-sm text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={form.is_public} onChange={set("is_public")} className="accent-[#00FF66]" data-testid="table-public"/>
            Pubblico (visibile a tutti)
          </label>
          <button disabled={loading} className="btn-primary w-full" data-testid="table-submit">{loading ? "..." : table ? "SALVA MODIFICHE" : "CREA TAVOLO"}</button>
        </form>
      </div>
    </div>
  );
}
